import type { ReactNode } from 'react';
import { Alert, Box, Chip, Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { StatusChip } from '../../components/common/StatusChip';

// The client half of the widget registry: how each widget id is drawn. WHICH widgets exist for a user, and what data they
// carry, is entirely the server's decision (modules/dashboard/dashboardWidgets.ts) - this file only knows how to draw the
// data it is handed. An id this build has no drawing for degrades to a notice instead of breaking the page.

export const inr = (n: unknown) => `₹${Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const int = (n: unknown) => Number(n ?? 0).toLocaleString('en-IN');
const date = (v: unknown) => (v ? new Date(String(v)).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '');
const COLORS = ['#44C47A', '#009688', '#B00020', '#0277BD', '#1B5E20'];

type D = Record<string, any>;

function Stat({ label, value, sub, color }: { label: string; value: ReactNode; sub?: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color: color ?? 'text.primary', lineHeight: 1.25 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}
const Stats = ({ children }: { children: ReactNode }) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{children}</Box>;
const Empty = ({ children }: { children: ReactNode }) => <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{children}</Typography>;

function MiniTable({ head, rows }: { head: Array<{ label: string; right?: boolean }>; rows: ReactNode[][] }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead><TableRow>{head.map((h) => <TableCell key={h.label} align={h.right ? 'right' : 'left'}>{h.label}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {rows.map((r, i) => <TableRow key={i} hover>{r.map((c, j) => <TableCell key={j} align={head[j]?.right ? 'right' : 'left'}>{c}</TableCell>)}</TableRow>)}
        </TableBody>
      </Table>
    </Box>
  );
}

const mono = { fontFamily: 'monospace', fontWeight: 600 } as const;
const StatusChips = ({ rows }: { rows: Array<{ status: string; count: number }> }) => (
  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>{rows.map((s) => <Chip key={s.status} size="small" label={`${s.status.replace(/_/g, ' ')}: ${s.count}`} />)}</Box>
);

export const WIDGET_VIEWS: Record<string, (p: { data: D }) => ReactNode> = {
  'sales.mySales': ({ data }) => (
    <>
      <Stats>
        <Stat label="Bills" value={int(data.billCount)} />
        <Stat label="Value" value={inr(data.lineValue)} sub="before discount & GST" color="#1B5E20" />
        <Stat label="Average bill" value={data.averageBill == null ? '—' : inr(data.averageBill)} />
        <Stat label="Pieces" value={int(data.pieces)} />
      </Stats>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{data.targetNote}</Typography>
    </>
  ),

  'sales.pendingEstimates': ({ data }) => (
    data.total === 0 ? <Empty>No open estimates</Empty> : (
      <>
        <StatusChips rows={data.byStatus ?? []} />
        <MiniTable head={[{ label: 'Estimate' }, { label: 'Customer' }, { label: 'Net payable', right: true }, { label: 'Status' }]}
          rows={(data.rows ?? []).map((e: D) => [<span style={mono}>{e.estimateNumber}</span>, e.customerName ?? '—', inr(e.netPayable), <StatusChip status={e.status} />])} />
      </>
    )
  ),

  'sales.branchSales': ({ data }) => (
    <>
      <Stats>
        <Stat label="Billed" value={inr(data.sales)} color="#1B5E20" />
        <Stat label="Bills" value={int(data.billCount)} />
        <Stat label="Average bill" value={data.averageBill == null ? '—' : inr(data.averageBill)} />
        <Stat label="Returns" value={inr(data.returnsAmount)} sub={`${int(data.returnsCount)} return(s)`} color={data.returnsCount ? '#B00020' : undefined} />
        <Stat label="Net of returns" value={inr(data.netSales)} />
      </Stats>
      {(data.byBranch ?? []).length > 1 && (
        <Box sx={{ mt: 2 }}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.byBranch}>
              <XAxis dataKey="branchName" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Number(v).toLocaleString('en-IN')} />
              <Tooltip formatter={(v) => inr(v)} />
              <Bar dataKey="sales" name="Billed" fill="#44C47A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </>
  ),

  'sales.employeePerformance': ({ data }) => (
    (data.rows ?? []).length === 0 ? <Empty>No sales in this period</Empty> : (
      <>
        <MiniTable head={[{ label: '#' }, { label: 'Employee' }, { label: 'Bills', right: true }, { label: 'Value', right: true }]}
          rows={data.rows.map((r: D, i: number) => [i + 1, r.name, int(r.billCount), inr(r.lineValue)])} />
        {data.truncated && <Alert severity="warning" sx={{ mt: 1 }}>Very large period - figures are partial. Pick a shorter range.</Alert>}
      </>
    )
  ),

  'sales.recentBills': ({ data }) => (
    (data.rows ?? []).length === 0 ? <Empty>No bills yet</Empty> : (
      <MiniTable head={[{ label: 'Bill' }, { label: 'Date' }, { label: 'Customer' }, { label: 'Amount', right: true }, { label: 'Status' }]}
        rows={data.rows.map((b: D) => [<span style={mono}>{b.invoiceNumber}</span>, date(b.invoiceDate), b.client, inr(b.totalAmount), <StatusChip status={b.status} />])} />
    )
  ),

  'sales.returnsAndVoids': ({ data }) => (
    <Stats>
      <Stat label="Sales returns" value={int(data.returnsCount)} sub={inr(data.returnsAmount)} color={data.returnsCount ? '#E65100' : undefined} />
      <Stat label="Voided bills" value={int(data.voidedCount)} sub={inr(data.voidedAmount)} color={data.voidedCount ? '#B00020' : undefined} />
    </Stats>
  ),

  'payments.receipts': ({ data }) => (
    (data.byMode ?? []).length === 0 ? <Empty>No receipts in this period</Empty> : (
      <>
        <Stat label="Total received" value={inr(data.total)} color="#1B5E20" />
        <MiniTable head={[{ label: 'Mode' }, { label: 'Receipts', right: true }, { label: 'Amount', right: true }]}
          rows={data.byMode.map((m: D) => [m.mode.replace(/_/g, ' '), int(m.count), inr(m.amount)])} />
      </>
    )
  ),

  'stock.summary': ({ data }) => (
    <>
      <Stats>
        <Stat label="In stock" value={int(data.inStock)} sub={`${Number(data.inStockGrossWeight).toFixed(2)} g gross`} color="#1B5E20" />
        <Stat label="On approval" value={int(data.onApproval)} sub="memo / consignment" />
        <Stat label="In transit" value={int(data.inTransit)} />
      </Stats>
      {(data.byBranch ?? []).length > 1 && (
        <MiniTable head={[{ label: 'Branch' }, { label: 'In stock', right: true }]} rows={data.byBranch.map((b: D) => [b.branchName, int(b.inStock)])} />
      )}
    </>
  ),

  'stock.costValue': ({ data }) => (
    <Stats><Stat label="Cost value" value={inr(data.costValue)} sub={`${int(data.pieces)} pieces in stock`} color="#1B5E20" /></Stats>
  ),

  'stock.slow': ({ data }) => (
    <Stats>
      <Stat label={`Untouched ${data.ageingDays}+ days`} value={int(data.slowPieces)} color={data.slowPieces ? '#E65100' : undefined} sub={`of ${int(data.inStock)} in stock`} />
    </Stats>
  ),

  'ops.pendingTransfers': ({ data }) => (
    data.total === 0 ? <Empty>No pending transfers</Empty> : (
      <>
        <StatusChips rows={data.byStatus ?? []} />
        <MiniTable head={[{ label: 'Transfer' }, { label: 'From → To' }, { label: 'Status' }]}
          rows={data.rows.map((t: D) => [<span style={mono}>{t.transferNumber}</span>, `${t.from} → ${t.to}`, <StatusChip status={t.status} />])} />
      </>
    )
  ),

  'ops.pendingApprovals': ({ data }) => (
    <Stats>
      <Stat label="Waiting" value={int(data.pending)} color={data.pending ? '#E65100' : '#1B5E20'}
        sub={data.pending && data.oldestRequestedAt ? `oldest since ${date(data.oldestRequestedAt)}` : 'nothing to decide'} />
    </Stats>
  ),

  'ops.overdueMemos': ({ data }) => (
    data.overdue === 0 ? <Empty>All memos are on time</Empty> : (
      <>
        <Stat label="Overdue" value={int(data.overdue)} color="#B00020" />
        <MiniTable head={[{ label: 'Memo' }, { label: 'Dealer' }, { label: 'Days overdue', right: true }]}
          rows={data.rows.map((m: D) => [<span style={mono}>{m.memoNumber}</span>, m.client, int(m.daysOverdue)])} />
      </>
    )
  ),

  'mfg.kpis': ({ data }) => (
    <Stats>
      <Stat label="Active orders" value={int(data.activeOrders)} color="#44C47A" />
      <Stat label="In production" value={int(data.inProduction)} color="#0277BD" />
      <Stat label="QC pending" value={int(data.qcPending)} color="#E65100" />
      <Stat label="Ready to deliver" value={int(data.readyDelivery)} color="#1B5E20" />
    </Stats>
  ),

  'mfg.metalVault': ({ data }) => (
    <Stats>
      <Stat label="Metal in vault" value={`${Number(data.availableGrams ?? 0).toFixed(2)} g`} sub="available gold + silver" color="#44C47A" />
      <Stat label="Today's GRNs" value={int(data.todayGrns)} color="#009688" />
    </Stats>
  ),

  'mfg.wip': ({ data }) => {
    const byProcess = (data.wipByProcess ?? []).reduce((acc: D[], c: D) => {
      const e = acc.find((a) => a.process === c.processName);
      if (e) e[c.status] = c.count; else acc.push({ process: c.processName, [c.status]: c.count });
      return acc;
    }, []);
    // Several (priority, status) rows were merged into one count per priority by the server's groupBy; sum defensively.
    const priority: D[] = [];
    for (const j of data.jobsByPriority ?? []) {
      const e = priority.find((p) => p.name === j.priority);
      if (e) e.value += j.count; else priority.push({ name: j.priority, value: j.count });
    }
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="caption" color="text.secondary">WIP by process stage</Typography>
          {byProcess.length === 0 ? <Empty>No active jobs</Empty> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byProcess}>
                <XAxis dataKey="process" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip />
                <Bar dataKey="IN_PROGRESS" fill="#44C47A" name="In progress" radius={[4, 4, 0, 0]} />
                <Bar dataKey="COMPLETED" fill="#009688" name="Completed" radius={[4, 4, 0, 0]} />
                <Bar dataKey="PENDING" fill="#E0E0E0" name="Pending" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Jobs by priority</Typography>
          {priority.length === 0 ? <Empty>No data</Empty> : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart><Pie data={priority} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65}>
                  {priority.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {priority.map((d, i) => <Chip key={d.name} label={`${d.name}: ${d.value}`} size="small" sx={{ bgcolor: COLORS[i % COLORS.length], color: '#FFF', fontWeight: 600, fontSize: 11 }} />)}
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    );
  },
};

export function WidgetBodySkeleton() {
  return <><Skeleton variant="text" width="40%" height={36} /><Skeleton variant="rectangular" height={64} /></>;
}
